import Close from "@/assets/icons/Close";
import InfoLine from "@/assets/icons/InfoLine";
import AppButton from "@/components/shared/AppButton";
import type { AppButtonProps } from "@/components/shared/AppButton";
import { Box } from "@/components/ui/box";
import { Divider } from "@/components/ui/divider";
import { Heading } from "@/components/ui/heading";
import { Modal, ModalBackdrop, ModalBody, ModalCloseButton, ModalContent, ModalFooter } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  children?: any;
  title: string;
  subtitle?: string;
  buttons?: AppButtonProps[];
  className?: string;
  noIcon?: boolean;
}

const BaseModal = ({ isOpen, onClose, children, title, subtitle, buttons, className, noIcon }: BaseModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalBackdrop />
      <ModalContent className={`bg-background-100 max-w-lg mx-auto rounded-2xl p-0 ${className}`}>
        <ModalCloseButton onPress={onClose} className="absolute top-4 right-4 z-10" data-test-id="close-button">
          <Close height={26} fill="text-typography-900" width={26} />
        </ModalCloseButton>

        <ModalBody className="py-8 px-5 m-0">
          <VStack className="gap-4">
            {!noIcon && (
              <Box className="items-center" data-test-id="info-icon">
                <InfoLine stroke="bg-typography-900" fill="none" height={25} width={25} />
              </Box>
            )}

            <Heading className="text-center text-typography-900 text-[18px] leading-[100%] font-bold">{title}</Heading>

            {subtitle && <Text className="text-typography-900 text-[16px] leading-[22px] text-center font-normal align-middle">{subtitle}</Text>}
            {children}
          </VStack>
        </ModalBody>

        <Divider className="bg-secondary-600" />

        {buttons && Array.isArray(buttons) && buttons.length > 0 && (
          <ModalFooter className="border-secondary-400 p-[20px] flex-row w-[100%] gap-[10px]">
            {buttons.map((buttonProps) => (
              <AppButton key={buttonProps.title} {...buttonProps} className="flex-1" />
            ))}
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default BaseModal;
